import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as LucideIcons from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Configure axios defaults for better mobile support
axios.defaults.timeout = 30000;
axios.defaults.headers.common['Content-Type'] = 'multipart/form-data';

// Available icons list
const ICON_LIST = [
  "Route", "Car", "Factory", "Trophy", "Truck", "ParkingCircle", "Warehouse",
  "Construction", "HardHat", "Paintbrush", "Brush", "Ruler", "PenTool",
  "Target", "CheckCircle", "Star", "Award", "Medal", "Flag",
  "Building", "Home", "Store", "Landmark", "MapPin",
  "Bike", "Bus", "Train", "Plane", "Ship",
  "Dumbbell", "Activity", "Timer", "Gauge", "Zap",
  "Shield", "Lock", "Eye", "Heart", "ThumbsUp"
];

// Dynamic Icon Component
const DynamicIcon = ({ name, className }) => {
  if (name && name.startsWith("data:")) {
    return (
      <img
        src={name}
        alt="icon"
        className={className}
        style={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }}
      />
    );
  }
  if (name && (name.startsWith("/api/uploads/") || name.startsWith("/api/site-images/"))) {
    return (
      <img 
        src={`${BACKEND_URL}${name}`} 
        alt="icon" 
        className={className}
        style={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }}
      />
    );
  }
  const IconComponent = LucideIcons[name] || LucideIcons.HelpCircle;
  return <IconComponent className={className} />;
};

// Login Component
const LoginForm = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("password", password);
      await axios.post(`${API}/admin/login`, formData);
      onLogin(password);
    } catch (err) {
      setError("Hibás jelszó");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#FFD700] flex items-center justify-center">
            <LucideIcons.Lock className="w-6 h-6 text-[#0F172A]" />
          </div>
        </div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-center text-[#0F172A] uppercase mb-8">Admin Belépés</h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Jelszó" data-testid="admin-password" className="w-full px-4 py-3 border-2 border-gray-200 focus:border-[#FFD700] outline-none text-lg" />
          {error && <p className="text-red-500 text-center">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full bg-[#FFD700] text-[#0F172A] py-4 font-bold uppercase tracking-wider hover:bg-[#E6C200] transition-colors disabled:opacity-50">
            {loading ? "Bejelentkezés..." : "Belépés"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

// Menu Editor Component
const MenuEditor = ({ password, onRefresh }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ label: "", href: "" });

  const fetchMenu = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/menu?password=${password}`);
      setMenuItems(response.data.items || []);
    } catch (err) {
      console.error("Error fetching menu:", err);
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("password", password);
      formData.append("items_json", JSON.stringify(menuItems));
      const response = await axios.put(`${API}/admin/menu`, formData);
      console.log("Menu save response:", response.data);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Menu save error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Ismeretlen hiba";
      alert("Hiba a mentés során: " + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    if (!newItem.label || !newItem.href) {
      alert("Add meg a menüpont nevét és linkjét!");
      return;
    }
    const item = {
      id: Date.now().toString(),
      label: newItem.label,
      href: newItem.href.startsWith("#") ? newItem.href : `#${newItem.href}`,
      order: menuItems.length
    };
    setMenuItems([...menuItems, item]);
    setNewItem({ label: "", href: "" });
  };

  const removeItem = (id) => {
    setMenuItems(menuItems.filter(item => item.id !== id));
  };

  const moveItem = (index, direction) => {
    const newItems = [...menuItems];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newItems.length) return;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    newItems.forEach((item, i) => item.order = i);
    setMenuItems(newItems);
  };

  const updateItem = (id, field, value) => {
    setMenuItems(menuItems.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" /></div>;

  return (
    <div className="bg-white p-6 border-2 border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase flex items-center gap-2">
          <LucideIcons.Menu className="w-5 h-5" /> Menü Szerkesztése
        </h3>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#0F172A] font-bold uppercase text-sm hover:bg-[#E6C200] disabled:opacity-50">
          <LucideIcons.Save className="w-4 h-4" /> {saving ? "Mentés..." : "Mentés"}
        </button>
      </div>

      {/* Current menu items */}
      <div className="space-y-3 mb-6">
        {menuItems.map((item, index) => (
          <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200">
            <div className="flex flex-col gap-1">
              <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-1 hover:bg-gray-200 disabled:opacity-30">
                <LucideIcons.ChevronUp className="w-4 h-4" />
              </button>
              <button onClick={() => moveItem(index, 1)} disabled={index === menuItems.length - 1} className="p-1 hover:bg-gray-200 disabled:opacity-30">
                <LucideIcons.ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <input type="text" value={item.label} onChange={(e) => updateItem(item.id, "label", e.target.value)} className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none" placeholder="Menüpont neve" />
            <input type="text" value={item.href} onChange={(e) => updateItem(item.id, "href", e.target.value)} className="w-40 px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none" placeholder="#szekció" />
            <button onClick={() => removeItem(item.id)} className="p-2 text-red-500 hover:bg-red-50">
              <LucideIcons.Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}
      </div>

      {/* Add new item */}
      <div className="flex items-center gap-3 p-3 bg-yellow-50 border-2 border-dashed border-[#FFD700]">
        <LucideIcons.Plus className="w-5 h-5 text-[#FFD700]" />
        <input type="text" value={newItem.label} onChange={(e) => setNewItem({ ...newItem, label: e.target.value })} className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none" placeholder="Új menüpont neve" />
        <input type="text" value={newItem.href} onChange={(e) => setNewItem({ ...newItem, href: e.target.value })} className="w-40 px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none" placeholder="#szekció" />
        <button onClick={addItem} className="px-4 py-2 bg-[#FFD700] text-[#0F172A] font-bold hover:bg-[#E6C200]">
          Hozzáadás
        </button>
      </div>

      <p className="mt-4 text-sm text-gray-500">
        Tipp: A link "#" karakterrel kezdődik, pl. #szolgaltatasok, #kapcsolat, vagy külső linkhez: https://...
      </p>
    </div>
  );
};

// Icon Selector Component
const IconSelector = ({ value, onChange, serviceNum, password }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleIconSelect = (iconName) => {
    onChange(iconName);
    setIsOpen(false);
  };

  const handleCustomUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("service_num", serviceNum);
    formData.append("password", password);
    
    try {
      const response = await axios.post(`${API}/admin/upload-icon`, formData);
      onChange(response.data.icon_url);
      setIsOpen(false);
    } catch (err) {
      alert("Hiba az ikon feltöltése során");
    } finally {
      setUploading(false);
    }
  };

  const isCustomIcon = value && (value.startsWith("/api/uploads/") || value.startsWith("/api/site-images/") || value.startsWith("data:"));

  return (
    <div className="relative">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 border border-gray-200 hover:border-[#FFD700] bg-white">
        <div className="w-8 h-8 bg-[#0F172A] flex items-center justify-center">
          <DynamicIcon name={value} className="w-5 h-5 text-[#FFD700]" />
        </div>
        <span className="text-sm">{isCustomIcon ? "Egyedi ikon" : value || "Válassz"}</span>
        <LucideIcons.ChevronDown className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-80 bg-white border-2 border-gray-200 shadow-lg p-4 max-h-80 overflow-y-auto">
          <p className="text-sm font-semibold mb-2">Előre definiált ikonok:</p>
          <div className="grid grid-cols-6 gap-2 mb-4">
            {ICON_LIST.map((iconName) => {
              const IconComp = LucideIcons[iconName];
              return (
                <button key={iconName} onClick={() => handleIconSelect(iconName)} className={`p-2 border ${value === iconName ? "border-[#FFD700] bg-yellow-50" : "border-gray-200"} hover:border-[#FFD700]`} title={iconName}>
                  {IconComp && <IconComp className="w-5 h-5" />}
                </button>
              );
            })}
          </div>
          
          <div className="border-t pt-4">
            <p className="text-sm font-semibold mb-2">Egyedi ikon feltöltése:</p>
            <label className="block">
              <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={handleCustomUpload} className="hidden" />
              <span className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 cursor-pointer border border-dashed border-gray-300">
                {uploading ? <div className="animate-spin w-4 h-4 border-2 border-[#FFD700] border-t-transparent rounded-full" /> : <LucideIcons.Upload className="w-4 h-4" />}
                {uploading ? "Feltöltés..." : "Kép kiválasztása"}
              </span>
            </label>
            <p className="text-xs text-gray-400 mt-1">PNG, SVG, JPEG (max 64x64 ajánlott)</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Content Editor Component
const ContentEditor = ({ password, onRefresh }) => {
  const [content, setContent] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/site-content?password=${password}`);
      setContent(response.data.content || {});
      setDefaults(response.data.defaults || {});
    } catch (err) {
      console.error("Error fetching content:", err);
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const handleChange = (key, value) => {
    setContent({ ...content, [key]: value });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("password", password);
      formData.append("content_json", JSON.stringify(content));
      const response = await axios.put(`${API}/admin/site-content`, formData);
      console.log("Content save response:", response.data);
      setSaved(true);
      if (onRefresh) onRefresh();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Content save error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Ismeretlen hiba";
      alert("Hiba a mentés során: " + errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" /></div>;

  const TextField = ({ keyName, label, multiline }) => (
    <div className="flex flex-col md:flex-row md:items-start gap-2">
      <label className="md:w-48 text-sm font-medium text-gray-600 pt-2">{label}</label>
      <div className="flex-1">
        {multiline ? (
          <textarea value={content[keyName] || ""} onChange={(e) => handleChange(keyName, e.target.value)} placeholder={defaults[keyName]} rows={3} className="w-full px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none text-sm resize-none" />
        ) : (
          <input type="text" value={content[keyName] || ""} onChange={(e) => handleChange(keyName, e.target.value)} placeholder={defaults[keyName]} className="w-full px-3 py-2 border border-gray-200 focus:border-[#FFD700] outline-none text-sm" />
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white p-4 border-2 border-gray-200 sticky top-20 z-30 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LucideIcons.Type className="w-5 h-5" />
          <span className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Szövegek Szerkesztése</span>
        </div>
        <button onClick={handleSave} disabled={saving} className={`flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-wider transition-colors ${saved ? "bg-green-500 text-white" : "bg-[#FFD700] text-[#0F172A] hover:bg-[#E6C200]"} disabled:opacity-50`}>
          {saving ? <><div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" /> Mentés...</> : saved ? <><LucideIcons.Check className="w-4 h-4" /> Mentve!</> : <><LucideIcons.Save className="w-4 h-4" /> Mentés</>}
        </button>
      </div>

      {/* Hero */}
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-4 border-b pb-2">Főoldal (Hero)</h3>
        <div className="space-y-4">
          <TextField keyName="hero_badge" label="Badge szöveg" />
          <TextField keyName="hero_title" label="Főcím" />
          <TextField keyName="hero_subtitle" label="Alcím" />
          <TextField keyName="hero_description" label="Leírás" multiline />
          <TextField keyName="hero_cta_primary" label="Elsődleges gomb" />
          <TextField keyName="hero_cta_secondary" label="Másodlagos gomb" />
        </div>
      </div>

      {/* Services */}
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-4 border-b pb-2">Szolgáltatások</h3>
        <div className="space-y-4">
          <TextField keyName="services_badge" label="Badge szöveg" />
          <TextField keyName="services_title" label="Szekció cím" />
          
          {[1, 2, 3].map((num) => (
            <div key={num} className="p-4 bg-gray-50 border border-gray-200 space-y-3">
              <div className="flex items-center gap-4">
                <span className="font-semibold text-sm w-32">{num}. szolgáltatás ikon:</span>
                <IconSelector value={content[`service_${num}_icon`] || defaults[`service_${num}_icon`]} onChange={(val) => handleChange(`service_${num}_icon`, val)} serviceNum={num} password={password} />
              </div>
              <TextField keyName={`service_${num}_title`} label={`${num}. szolgáltatás neve`} />
              <TextField keyName={`service_${num}_description`} label={`${num}. szolgáltatás leírása`} multiline />
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-4 border-b pb-2">Galéria</h3>
        <div className="space-y-4">
          <TextField keyName="gallery_badge" label="Badge szöveg" />
          <TextField keyName="gallery_title" label="Szekció cím" />
          <TextField keyName="gallery_description" label="Leírás" multiline />
        </div>
      </div>

      {/* Contact */}
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-4 border-b pb-2">Kapcsolat</h3>
        <div className="space-y-4">
          <TextField keyName="contact_badge" label="Badge szöveg" />
          <TextField keyName="contact_title" label="Szekció cím" />
          <TextField keyName="contact_description" label="Leírás" multiline />
          <TextField keyName="contact_email" label="E-mail cím" />
          <TextField keyName="contact_phone" label="Telefonszám" />
          <TextField keyName="contact_address" label="Cím" />
          <TextField keyName="contact_hours" label="Nyitvatartás" />
          <TextField keyName="contact_cta_email" label="Email gomb" />
          <TextField keyName="contact_cta_phone" label="Telefon gomb" />
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase mb-4 border-b pb-2">Lábléc</h3>
        <div className="space-y-4">
          <TextField keyName="footer_copyright" label="Copyright szöveg" />
          <p className="text-xs text-gray-500">Használd a {"{year}"} változót az aktuális évszámhoz.</p>
        </div>
      </div>
    </div>
  );
};

// Site Images Manager
const SiteImagesManager = ({ password }) => {
  const [siteImages, setSiteImages] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);

  const imageLabels = {
    hero: "Főoldal Háttérkép",
    hero_side: "Főoldal Jobb Oldali Kép",
    service_utburkolat: "Szolgáltatás - Útburkolati Jelek Festése",
    service_parkolo: "Szolgáltatás - Parkoló Festés",
    service_ipari: "Szolgáltatás - Ipari Padlójelölés",
    service_sport: "Szolgáltatás - Sportpálya Vonalazás",
    contact: "Kapcsolat Szekció Kép"
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await axios.get(`${API}/admin/site-settings?password=${password}`);
        setSiteImages(response.data.images || {});
        setDefaults(response.data.defaults || {});
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetch();
  }, [password]);

  const handleUpload = async (key, file) => {
    setUploading(key);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("image_key", key);
    formData.append("password", password);
    try {
      const response = await axios.post(`${API}/admin/site-settings/upload`, formData);
      setSiteImages({ ...siteImages, [key]: response.data.url });
    } catch (err) { alert("Hiba a feltöltés során"); }
    finally { setUploading(null); }
  };

  const handleReset = async (key) => {
    if (!window.confirm("Visszaállítod az alapértelmezett képet?")) return;
    try {
      await axios.delete(`${API}/admin/site-settings/${key}?password=${password}`);
      const newImages = { ...siteImages };
      delete newImages[key];
      setSiteImages(newImages);
    } catch (err) { alert("Hiba"); }
  };

  const getImageUrl = (key) => {
    const url = siteImages[key] || defaults[key];
    if (!url) return "";
    if (url.startsWith("data:")) return url;
    return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  };

  const isCustom = (key) => {
    const v = siteImages[key];
    return v && (v.startsWith("/api/uploads/") || v.startsWith("/api/site-images/") || v.startsWith("data:"));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-10 h-10 border-4 border-[#FFD700] border-t-transparent rounded-full" /></div>;

  return (
    <div className="bg-white p-6 border-2 border-gray-200">
      <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-6 flex items-center gap-2">
        <LucideIcons.Settings className="w-5 h-5" /> Weboldal Képek
      </h3>
      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(imageLabels).map(([key, label]) => (
          <div key={key} className="border border-gray-200 p-4">
            <h4 className="font-semibold mb-3">{label}</h4>
            <div className="relative aspect-video mb-3 bg-gray-100 overflow-hidden">
              <img src={getImageUrl(key)} alt={label} className="w-full h-full object-cover" />
              {uploading === key && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full" /></div>}
              {isCustom(key) && <span className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1">Saját</span>}
            </div>
            <div className="flex gap-2">
              <label className="flex-1">
                <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleUpload(key, e.target.files[0])} className="hidden" />
                <span className="block w-full text-center bg-[#FFD700] text-[#0F172A] py-2 px-4 font-bold text-sm uppercase cursor-pointer hover:bg-[#E6C200]">
                  <LucideIcons.Upload className="w-4 h-4 inline mr-2" />Csere
                </span>
              </label>
              {isCustom(key) && <button onClick={() => handleReset(key)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300"><LucideIcons.RefreshCw className="w-4 h-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Gallery Manager
const GalleryManager = ({ images, password, onDelete, onRefresh }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingId, setSavingId] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm("Biztosan törölni szeretnéd ezt a képet?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/images/${id}?password=${password}`);
      onDelete(id);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Hiba a törlés során");
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (image, index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= images.length) return;
    const other = images[newIndex];
    const formData1 = new FormData(); formData1.append("order", other.order); formData1.append("password", password);
    const formData2 = new FormData(); formData2.append("order", image.order); formData2.append("password", password);
    try {
      await Promise.all([axios.put(`${API}/admin/images/${image.id}`, formData1), axios.put(`${API}/admin/images/${other.id}`, formData2)]);
      onRefresh();
    } catch (err) { alert("Hiba a sorrend módosításakor"); }
  };

  const startEditing = (image) => {
    setEditingId(image.id);
    setEditText(image.alt_text || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditText("");
  };

  const saveAltText = async (imageId) => {
    setSavingId(imageId);
    try {
      const formData = new FormData();
      formData.append("alt_text", editText);
      formData.append("password", password);
      await axios.put(`${API}/admin/images/${imageId}`, formData);
      setEditingId(null);
      setEditText("");
      onRefresh();
    } catch (err) {
      console.error("Save error:", err);
      alert("Hiba a mentés során");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white p-6 border-2 border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase flex items-center gap-2">
          <LucideIcons.Image className="w-5 h-5" /> Referencia Galéria ({images.length} kép)
        </h3>
        <button 
          onClick={onRefresh} 
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm"
        >
          <LucideIcons.RefreshCw className="w-4 h-4" /> Frissítés
        </button>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        A feltöltött képek azonnal megjelennek a weboldal "Referenciák" szekciójában. 
        Kattints a leírásra a szerkesztéshez, vagy használd a gombokat a mozgatáshoz/törléshez.
      </p>

      {images.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-200">
          <LucideIcons.ImageOff className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 mb-2">Még nincsenek feltöltött referencia képek.</p>
          <p className="text-sm text-gray-400">Használd a fenti feltöltő mezőt új képek hozzáadásához.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <div key={image.id} className="border border-gray-200 overflow-hidden bg-gray-50">
              {/* Image with action buttons */}
              <div className="relative group aspect-square">
                <img 
                  src={`${BACKEND_URL}${image.url}`} 
                  alt={image.alt_text} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="50" text-anchor="middle" fill="%239ca3af" font-size="12">Hiba</text></svg>';
                  }}
                />
                {/* Hover overlay with action buttons */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button 
                    onClick={() => handleMove(image, index, -1)} 
                    disabled={index === 0} 
                    className="p-2 bg-white/20 hover:bg-white/40 disabled:opacity-30 rounded"
                    title="Balra mozgatás"
                  >
                    <LucideIcons.ArrowLeft className="w-4 h-4 text-white" />
                  </button>
                  <button 
                    onClick={() => handleMove(image, index, 1)} 
                    disabled={index === images.length - 1} 
                    className="p-2 bg-white/20 hover:bg-white/40 disabled:opacity-30 rounded"
                    title="Jobbra mozgatás"
                  >
                    <LucideIcons.ArrowRight className="w-4 h-4 text-white" />
                  </button>
                  <button 
                    onClick={() => handleDelete(image.id)} 
                    disabled={deletingId === image.id}
                    className="p-2 bg-red-500/80 hover:bg-red-600 rounded disabled:opacity-50"
                    title="Törlés"
                  >
                    {deletingId === image.id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <LucideIcons.Trash2 className="w-4 h-4 text-white" />
                    )}
                  </button>
                </div>
              </div>
              {/* Editable description - always accessible */}
              <div className="p-2 bg-white border-t border-gray-100">
                {editingId === image.id ? (
                  <div className="flex flex-col gap-1">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full px-2 py-1 text-xs border border-gray-300 focus:border-[#FFD700] outline-none"
                      placeholder="Kép leírása"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') saveAltText(image.id);
                        if (e.key === 'Escape') cancelEditing();
                      }}
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={() => saveAltText(image.id)}
                        disabled={savingId === image.id}
                        className="flex-1 px-2 py-1 bg-[#FFD700] text-[#0F172A] text-xs font-bold hover:bg-[#E6C200] disabled:opacity-50"
                      >
                        {savingId === image.id ? "..." : "Mentés"}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="px-2 py-1 bg-gray-200 text-gray-600 text-xs hover:bg-gray-300"
                      >
                        Mégse
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => startEditing(image)}
                    className="flex items-center gap-1 cursor-pointer hover:bg-gray-50 -m-2 p-2"
                    title="Kattints a szerkesztéshez"
                  >
                    <p className="text-xs text-gray-600 truncate flex-1" title={image.alt_text}>
                      {image.alt_text || "Nincs leírás - kattints a hozzáadáshoz"}
                    </p>
                    <LucideIcons.Pencil className="w-3 h-3 text-[#FFD700] flex-shrink-0" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Image Upload
const ImageUpload = ({ password, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [altText, setAltText] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFile = async (file) => {
    if (!file.type.startsWith("image/")) { alert("Csak kép!"); return; }
    setUploading(true);
    setUploadSuccess(false);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("alt_text", altText || file.name.split('.')[0]);
    formData.append("password", password);
    try {
      const response = await axios.post(`${API}/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for uploads
      });
      onUpload(response.data.image);
      setAltText("");
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err) {
      console.error("Upload error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Ismeretlen hiba";
      alert("Hiba a feltöltés során: " + errorMsg);
    }
    finally { setUploading(false); }
  };

  return (
    <div className="bg-white p-6 border-2 border-gray-200">
      <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-4 flex items-center gap-2">
        <LucideIcons.Upload className="w-5 h-5" /> Új Referencia Kép Feltöltése
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Tölts fel saját képeket a munkáidról. A feltöltött képek azonnal megjelennek a weboldalon.
      </p>
      <input 
        type="text" 
        value={altText} 
        onChange={(e) => setAltText(e.target.value)} 
        placeholder="Kép leírása (pl. Parkoló festés - Budapest)" 
        className="w-full px-4 py-2 border border-gray-200 focus:border-[#FFD700] outline-none mb-4" 
      />
      <label className={`border-2 border-dashed p-8 text-center cursor-pointer block transition-colors ${uploadSuccess ? 'border-green-500 bg-green-50' : 'hover:border-[#FFD700]'}`}>
        <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])} className="hidden" />
        {uploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full mb-2" />
            <p>Feltöltés folyamatban...</p>
          </div>
        ) : uploadSuccess ? (
          <div className="flex flex-col items-center text-green-600">
            <LucideIcons.CheckCircle className="w-12 h-12 mb-4" />
            <p className="font-semibold">Sikeresen feltöltve!</p>
          </div>
        ) : (
          <>
            <LucideIcons.Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="font-medium">Húzd ide a képet vagy kattints a kiválasztáshoz</p>
            <p className="text-sm text-gray-500 mt-2">Max 10MB, JPEG/PNG/WebP formátum</p>
          </>
        )}
      </label>
    </div>
  );
};

// Video Manager — admin upload/list/delete/alt-edit for self-hosted videos
const VideoManager = ({ password }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [altText, setAltText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingAlt, setEditingAlt] = useState("");

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/videos?password=${password}`);
      setVideos(res.data.videos || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [password]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      alert("A videó maximum 100MB lehet");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("alt", altText || file.name);
      form.append("password", password);
      await axios.post(`${API}/admin/videos/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setAltText("");
      await fetchVideos();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      alert("Hiba a feltöltés során: " + msg);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Biztosan törlöd ezt a videót?")) return;
    try {
      await axios.delete(`${API}/admin/videos/${id}?password=${password}`);
      setVideos(videos.filter((v) => v.id !== id));
    } catch (e) {
      alert("Hiba a törlés során");
    }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setEditingAlt(v.alt || "");
  };

  const saveEdit = async (id) => {
    try {
      const form = new FormData();
      form.append("alt", editingAlt);
      form.append("password", password);
      await axios.put(`${API}/admin/videos/${id}`, form);
      setEditingId(null);
      await fetchVideos();
    } catch (e) {
      alert("Hiba a mentés során");
    }
  };

  const fmtSize = (b) => {
    if (b > 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    if (b > 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${b} B`;
  };

  return (
    <div className="space-y-6" data-testid="video-manager">
      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-4 flex items-center gap-2">
          <LucideIcons.Video className="w-5 h-5" /> Új videó feltöltése
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Tölts fel saját videókat (MP4, WebM, MOV) — felhőalapú tárolásba mentődnek, deploy után is megmaradnak.
        </p>
        <input
          type="text"
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="Videó leírása (pl. Parkoló festés Budapest)"
          className="w-full px-4 py-2 border border-gray-200 focus:border-[#FFD700] outline-none mb-4"
          data-testid="video-alt-input"
        />
        <label className={`border-2 border-dashed p-8 text-center cursor-pointer block transition-colors ${uploading ? "border-[#FFD700] bg-yellow-50" : "hover:border-[#FFD700]"}`}>
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/ogg"
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            className="hidden"
            data-testid="video-file-input"
          />
          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full mb-2" />
              <p>Feltöltés folyamatban... {progress}%</p>
              <div className="mt-3 w-full max-w-xs h-2 bg-gray-200 rounded overflow-hidden">
                <div className="h-full bg-[#FFD700] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <>
              <LucideIcons.UploadCloud className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="font-medium">Húzd ide a videót vagy kattints a kiválasztáshoz</p>
              <p className="text-sm text-gray-500 mt-2">Max 100MB, MP4 / WebM / MOV / OGG</p>
            </>
          )}
        </label>
      </div>

      <div className="bg-white p-6 border-2 border-gray-200">
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-4 flex items-center gap-2">
          <LucideIcons.ListVideo className="w-5 h-5" /> Feltöltött videók ({videos.length})
        </h3>
        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-center text-gray-500 py-8">Még nincs feltöltött videó.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <div key={v.id} className="border border-gray-200 rounded overflow-hidden" data-testid={`video-row-${v.id}`}>
                <div className="bg-black aspect-video">
                  <video
                    src={`${BACKEND_URL}${v.src}`}
                    className="w-full h-full object-contain"
                    controls
                    preload="metadata"
                    playsInline
                  />
                </div>
                <div className="p-3 space-y-2">
                  {editingId === v.id ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingAlt}
                        onChange={(e) => setEditingAlt(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-200 text-sm outline-none focus:border-[#FFD700]"
                      />
                      <button onClick={() => saveEdit(v.id)} className="px-2 py-1 bg-green-600 text-white text-xs"><LucideIcons.Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditingId(null)} className="px-2 py-1 bg-gray-300 text-xs"><LucideIcons.X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate" title={v.alt}>{v.alt || "(nincs leírás)"}</p>
                      <button onClick={() => startEdit(v)} className="text-yellow-600 hover:text-yellow-700" title="Leírás szerkesztése">
                        <LucideIcons.Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{fmtSize(v.size)} · {v.content_type}</span>
                    <button onClick={() => handleDelete(v.id)} className="text-red-600 hover:text-red-700" data-testid={`video-delete-${v.id}`}>
                      <LucideIcons.Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Dashboard
const AdminDashboard = ({ password, onLogout }) => {
  const [activeTab, setActiveTab] = useState("content");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/images?password=${password}`);
      setImages(res.data.images);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [password]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { id: "content", label: "Szövegek", icon: LucideIcons.Type },
    { id: "menu", label: "Menü", icon: LucideIcons.Menu },
    { id: "images", label: "Képek", icon: LucideIcons.Settings },
    { id: "gallery", label: "Galéria", icon: LucideIcons.Image },
    { id: "videos", label: "Videók", icon: LucideIcons.Video },
  ];

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <header className="bg-[#0F172A] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase">PaintRoad Admin</h1>
          <nav className="flex items-center gap-2">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 transition-colors ${activeTab === tab.id ? "bg-[#FFD700] text-[#0F172A]" : "hover:text-[#FFD700]"}`}>
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
            <a href="/" className="flex items-center gap-2 px-4 py-2 hover:text-[#FFD700]"><LucideIcons.Eye className="w-4 h-4" /> Weboldal</a>
            <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2 hover:text-red-400"><LucideIcons.LogOut className="w-4 h-4" /> Kilépés</button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === "content" && <ContentEditor password={password} onRefresh={fetchData} />}
        {activeTab === "menu" && <MenuEditor password={password} onRefresh={fetchData} />}
        {activeTab === "images" && <SiteImagesManager password={password} />}
        {activeTab === "gallery" && (
          <div className="space-y-6">
            <ImageUpload password={password} onUpload={(img) => setImages([...images, img])} />
            <GalleryManager images={images} password={password} onDelete={(id) => setImages(images.filter(i => i.id !== id))} onRefresh={fetchData} />
          </div>
        )}
        {activeTab === "videos" && <VideoManager password={password} />}
      </main>
    </div>
  );
};

// Main Admin
function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    const saved = sessionStorage.getItem("adminPassword");
    if (saved) { setPassword(saved); setIsLoggedIn(true); }
  }, []);

  const handleLogin = (pwd) => { setPassword(pwd); setIsLoggedIn(true); sessionStorage.setItem("adminPassword", pwd); };
  const handleLogout = () => { setPassword(""); setIsLoggedIn(false); sessionStorage.removeItem("adminPassword"); };

  if (!isLoggedIn) return <LoginForm onLogin={handleLogin} />;
  return <AdminDashboard password={password} onLogout={handleLogout} />;
}

export default Admin;
